/**
 * Admin > DIDs / TFNs
 *
 * Inventory + per-row edit + sync button. Admin-only (route gate enforces).
 */
import React, { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { didsApi, queuesApi, clientsApi } from '../../../api/client';
import HelpButton from '../../help/HelpButton';

const DEST_TYPES = [
  { v: 'queue',            l: 'Queue' },
  { v: 'agent_extension',  l: 'Agent extension' },
  { v: 'ivr_context',      l: 'IVR context' },
  { v: 'voicemail',        l: 'Voicemail box' },
  { v: 'asterisk_context', l: 'Asterisk context (advanced)' },
];

export default function DidsPage() {
  const qc = useQueryClient();
  const [filter, setFilter] = useState('');
  const [editing, setEditing] = useState(null); // null | 'new' | <did>
  const [importing, setImporting] = useState(false);
  const [syncToast, setSyncToast] = useState(null);

  const { data: dids = [], isLoading } = useQuery({
    queryKey: ['admin', 'dids'],
    queryFn: () => didsApi.list(),
  });
  const { data: queues = [] } = useQuery({
    queryKey: ['admin', 'queues'],
    queryFn: () => queuesApi.list(),
    staleTime: 60_000,
  });
  const { data: clients = [] } = useQuery({
    queryKey: ['admin', 'clients'],
    queryFn: () => clientsApi.list(),
    staleTime: 60_000,
  });

  const clientNameById = useMemo(
    () => Object.fromEntries(clients.map((c) => [c.id, c.name])),
    [clients]
  );

  const syncMut = useMutation({
    mutationFn: () => didsApi.sync(),
    onSuccess: (r) => {
      setSyncToast({ kind: 'ok', msg: `Pushed ${Object.keys(r.summary?.dids || {}).length} DID(s). Tag ${r.summary?.tag}.` });
      qc.invalidateQueries({ queryKey: ['admin', 'dids'] });
      setTimeout(() => setSyncToast(null), 5000);
    },
    onError: (e) => {
      setSyncToast({ kind: 'err', msg: String(e.detail || e.message) });
      setTimeout(() => setSyncToast(null), 8000);
    },
  });

  const removeMut = useMutation({
    mutationFn: (id) => didsApi.remove(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'dids'] }),
  });

  const filtered = useMemo(() => {
    if (!filter.trim()) return dids;
    const q = filter.trim().toLowerCase();
    return dids.filter((d) =>
      (d.number || '').toLowerCase().includes(q) ||
      (d.name || '').toLowerCase().includes(q) ||
      (d.dest_value || '').toLowerCase().includes(q) ||
      (d.notes || '').toLowerCase().includes(q)
    );
  }, [dids, filter]);

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <div className="flex items-baseline justify-between mb-4 gap-2 flex-wrap">
        <div>
          <h1 className="text-lg font-semibold">DIDs / TFNs</h1>
          <p className="text-sm text-surface-muted dark:text-surface-dark-muted">
            Inbound numbers and their Asterisk routing. Changes here update the database; click <strong>Sync to LOU Issabel</strong> to push to the dialplan.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <HelpButton moduleKey="admin_dids" />
          <input
            type="search"
            placeholder="Filter number / name / dest"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="px-2 py-1 bg-surface-base dark:bg-surface-dark-base border border-surface-border dark:border-surface-dark-border rounded-thin text-sm w-56"
          />
          <button
            onClick={() => setImporting(true)}
            className="px-3 py-1.5 rounded-thin border border-surface-border dark:border-surface-dark-border text-sm"
          >
            ↑ Import CSV
          </button>
          <button
            onClick={() => setEditing('new')}
            className="px-3 py-1.5 rounded-thin bg-continuum-500 hover:bg-continuum-600 text-white text-sm"
          >
            + New DID
          </button>
          <button
            onClick={() => syncMut.mutate()}
            disabled={syncMut.isPending}
            className="px-3 py-1.5 rounded-thin bg-amber-500 hover:bg-amber-600 disabled:opacity-40 text-white text-sm"
            title="Push the inventory to LOU Issabel's ext-did-custom + reload dialplan"
          >
            {syncMut.isPending ? 'Syncing…' : '↻ Sync to LOU Issabel'}
          </button>
        </div>
      </div>

      {syncToast && (
        <div className={
          'mb-3 px-3 py-2 rounded-thin text-sm ' +
          (syncToast.kind === 'ok'
            ? 'bg-emerald-100 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-200'
            : 'bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-200')
        }>{syncToast.msg}</div>
      )}

      {isLoading ? (
        <div className="text-sm text-surface-muted">Loading…</div>
      ) : (
        <div className="border border-surface-border dark:border-surface-dark-border rounded-thin overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-surface-subtle dark:bg-surface-dark-subtle">
              <tr>
                <th className="px-3 py-2 text-left font-medium">Number</th>
                <th className="px-3 py-2 text-left font-medium">Name</th>
                <th className="px-3 py-2 text-left font-medium">Destination</th>
                <th className="px-3 py-2 text-left font-medium">Failover</th>
                <th className="px-3 py-2 text-left font-medium">Tenant</th>
                <th className="px-3 py-2 text-left font-medium">Carrier</th>
                <th className="px-3 py-2 text-left font-medium">Status</th>
                <th className="px-3 py-2 text-left font-medium">Synced</th>
                <th className="px-3 py-2 text-right font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={9} className="px-3 py-6 text-center text-surface-muted">
                    No DIDs yet. Click <em>+ New DID</em> to add one.
                  </td>
                </tr>
              )}
              {filtered.map((d) => (
                <tr key={d.id} className="border-t border-surface-border dark:border-surface-dark-border">
                  <td className="px-3 py-2 font-mono">{d.number}</td>
                  <td className="px-3 py-2">{d.name}</td>
                  <td className="px-3 py-2">
                    <DestPill type={d.dest_type} value={d.dest_value} />
                  </td>
                  <td className="px-3 py-2">
                    {d.failover_dest_value ? (
                      <DestPill type={d.failover_dest_type} value={d.failover_dest_value} />
                    ) : <span className="text-xs text-surface-muted">—</span>}
                  </td>
                  <td className="px-3 py-2 text-xs">
                    {d.client_id ? (clientNameById[d.client_id] || '(unknown)')
                                : <span className="text-surface-muted">Continuum</span>}
                  </td>
                  <td className="px-3 py-2 text-xs">{d.carrier}</td>
                  <td className="px-3 py-2">
                    {d.enabled
                      ? <span className="px-2 py-0.5 rounded-thin text-xs bg-emerald-100 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-200">enabled</span>
                      : <span className="px-2 py-0.5 rounded-thin text-xs bg-surface-subtle text-surface-muted">disabled</span>}
                  </td>
                  <td className="px-3 py-2 text-xs whitespace-nowrap">
                    {d.last_sync_error
                      ? <span title={d.last_sync_error} className="text-red-600">⚠ error</span>
                      : d.last_synced_at
                        ? <span className="text-emerald-600" title={new Date(d.last_synced_at).toLocaleString()}>✓ {timeAgo(d.last_synced_at)}</span>
                        : <span className="text-amber-600">never</span>}
                  </td>
                  <td className="px-3 py-2 text-right whitespace-nowrap space-x-3">
                    <button onClick={() => setEditing(d)} className="text-continuum-600 dark:text-continuum-200 text-sm">Edit</button>
                    <button onClick={() => { if (window.confirm(`Delete DID ${d.number}?`)) removeMut.mutate(d.id); }} className="text-red-600 text-sm">Delete</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {editing && (
        <DidEditModal
          did={editing === 'new' ? null : editing}
          queues={queues}
          clients={clients}
          onClose={() => { setEditing(null); qc.invalidateQueries({ queryKey: ['admin', 'dids'] }); }}
        />
      )}

      {importing && (
        <CsvImportModal onClose={() => { setImporting(false); qc.invalidateQueries({ queryKey: ['admin', 'dids'] }); }} />
      )}
    </div>
  );
}


function timeAgo(iso) {
  if (!iso) return '';
  const s = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return `${Math.round(s)}s ago`;
  if (s < 3600) return `${Math.round(s / 60)}m ago`;
  if (s < 86400) return `${Math.round(s / 3600)}h ago`;
  return `${Math.round(s / 86400)}d ago`;
}


function DestPill({ type, value }) {
  const colors = {
    queue:            'bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-200',
    agent_extension:  'bg-purple-100 dark:bg-purple-900/40 text-purple-700 dark:text-purple-200',
    ivr_context:      'bg-indigo-100 dark:bg-indigo-900/40 text-indigo-700 dark:text-indigo-200',
    voicemail:        'bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-200',
    asterisk_context: 'bg-surface-subtle text-surface-muted',
  };
  const label = (DEST_TYPES.find((t) => t.v === type) || {}).l || type;
  return (
    <span className={'inline-flex items-center gap-1 px-2 py-0.5 rounded-thin text-xs ' + (colors[type] || colors.asterisk_context)}>
      <span className="opacity-80">{label}</span>
      <span className="font-mono">{value}</span>
    </span>
  );
}


// ────────────────────────────────────────────────────────────────────
// Edit modal
// ────────────────────────────────────────────────────────────────────


function DidEditModal({ did, queues, clients, onClose }) {
  const isNew = !did;
  const qc = useQueryClient();
  const [form, setForm] = useState(() => ({
    number:              did?.number              || '',
    name:                did?.name                || '',
    carrier:             did?.carrier             || 'telnyx',
    monthly_cost:        did?.monthly_cost ?? '',
    dest_type:           did?.dest_type           || 'queue',
    dest_value:          did?.dest_value          || '',
    failover_dest_type:  did?.failover_dest_type  || '',
    failover_dest_value: did?.failover_dest_value || '',
    cnam_lookup:         did?.cnam_lookup ?? false,
    geo_block_countries: (did?.geo_block_countries || []).join(','),
    enabled:             did?.enabled ?? true,
    client_id:           did?.client_id || '',
    notes:               did?.notes || '',
  }));
  const [error, setError] = useState(null);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = useMutation({
    mutationFn: () => {
      const payload = {
        number: form.number.trim(),
        name: form.name.trim(),
        carrier: form.carrier.trim() || 'telnyx',
        monthly_cost: form.monthly_cost === '' ? null : Number(form.monthly_cost),
        dest_type: form.dest_type,
        dest_value: form.dest_value.trim(),
        failover_dest_type: form.failover_dest_type || null,
        failover_dest_value: form.failover_dest_value.trim() || null,
        cnam_lookup: !!form.cnam_lookup,
        geo_block_countries: form.geo_block_countries
          .split(',')
          .map((s) => s.trim().toUpperCase())
          .filter(Boolean),
        enabled: !!form.enabled,
        client_id: form.client_id || null,
        notes: form.notes.trim() || null,
      };
      return isNew ? didsApi.create(payload) : didsApi.update(did.id, payload);
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['admin', 'dids'] }); onClose(); },
    onError: (e) => setError(e.detail || e.message),
  });

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <div className="w-full max-w-2xl bg-surface-panel dark:bg-surface-dark-panel border border-surface-border dark:border-surface-dark-border rounded-thin shadow-lg">
        <div className="px-4 py-3 border-b border-surface-border dark:border-surface-dark-border flex items-center justify-between">
          <h3 className="font-semibold">{isNew ? 'New DID' : `Edit DID ${did.number}`}</h3>
          <button onClick={onClose} className="text-surface-muted">✕</button>
        </div>
        <div className="p-4 space-y-3 text-sm">
          <div className="grid grid-cols-2 gap-3">
            <Field label="Number (E.164)">
              <input value={form.number} onChange={(e) => set('number', e.target.value)}
                placeholder="+19294510060" className="input font-mono" />
            </Field>
            <Field label="Friendly name">
              <input value={form.name} onChange={(e) => set('name', e.target.value)}
                placeholder="Demo Sales Inbound" className="input" />
            </Field>
            <Field label="Carrier">
              <input value={form.carrier} onChange={(e) => set('carrier', e.target.value)} className="input" />
            </Field>
            <Field label="Monthly cost ($, optional)">
              <input value={form.monthly_cost} onChange={(e) => set('monthly_cost', e.target.value)}
                type="number" step="0.01" min="0" className="input font-mono" />
            </Field>
          </div>

          <div className="border-t border-surface-border dark:border-surface-dark-border pt-3">
            <div className="text-xs uppercase tracking-wider text-surface-muted mb-2">Destination</div>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Type">
                <select value={form.dest_type} onChange={(e) => set('dest_type', e.target.value)} className="input">
                  {DEST_TYPES.map((t) => <option key={t.v} value={t.v}>{t.l}</option>)}
                </select>
              </Field>
              <Field label="Value">
                <DestValueInput
                  type={form.dest_type}
                  value={form.dest_value}
                  onChange={(v) => set('dest_value', v)}
                  queues={queues}
                />
              </Field>
            </div>
          </div>

          <details className="border-t border-surface-border dark:border-surface-dark-border pt-3">
            <summary className="cursor-pointer text-xs text-surface-muted">Failover destination (optional)</summary>
            <div className="grid grid-cols-2 gap-3 mt-2">
              <Field label="Type">
                <select value={form.failover_dest_type} onChange={(e) => set('failover_dest_type', e.target.value)} className="input">
                  <option value="">— none —</option>
                  {DEST_TYPES.map((t) => <option key={t.v} value={t.v}>{t.l}</option>)}
                </select>
              </Field>
              <Field label="Value">
                <DestValueInput
                  type={form.failover_dest_type}
                  value={form.failover_dest_value}
                  onChange={(v) => set('failover_dest_value', v)}
                  queues={queues}
                />
              </Field>
            </div>
          </details>

          <div className="grid grid-cols-2 gap-3 pt-1">
            <Field label="Tenant (client)">
              <select value={form.client_id} onChange={(e) => set('client_id', e.target.value)} className="input">
                <option value="">Continuum (no tenant)</option>
                {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </Field>
            <Field label="Geo-block countries (CSV, ISO-2)">
              <input value={form.geo_block_countries} onChange={(e) => set('geo_block_countries', e.target.value)}
                placeholder="RU, KP" className="input font-mono uppercase" />
            </Field>
          </div>

          <Field label="Notes">
            <textarea value={form.notes} onChange={(e) => set('notes', e.target.value)} rows={2} className="input" />
          </Field>

          <div className="flex gap-4 text-xs">
            <label className="flex items-center gap-1">
              <input type="checkbox" checked={form.enabled} onChange={(e) => set('enabled', e.target.checked)} />
              Enabled
            </label>
            <label className="flex items-center gap-1">
              <input type="checkbox" checked={form.cnam_lookup} onChange={(e) => set('cnam_lookup', e.target.checked)} />
              CNAM lookup (placeholder)
            </label>
          </div>

          {error && <div className="text-red-600 text-sm">{String(error)}</div>}

          <p className="text-xs text-surface-muted">
            Saving here writes to the database only. Click <strong>Sync to LOU Issabel</strong> from the DIDs page to push to Asterisk's dialplan.
          </p>
        </div>
        <div className="px-4 py-3 border-t border-surface-border dark:border-surface-dark-border flex justify-end gap-2">
          <button onClick={onClose} className="px-3 py-1.5 rounded-thin border border-surface-border dark:border-surface-dark-border text-sm">Cancel</button>
          <button
            disabled={!form.number.trim() || !form.name.trim() || !form.dest_value.trim() || save.isPending}
            onClick={() => save.mutate()}
            className="px-3 py-1.5 rounded-thin bg-continuum-500 hover:bg-continuum-600 disabled:opacity-40 text-white text-sm"
          >
            {save.isPending ? 'Saving…' : 'Save'}
          </button>
        </div>
        <style>{`.input { width: 100%; padding: 4px 8px; background: transparent; border: 1px solid currentColor; border-color: rgb(209 213 219 / 0.6); border-radius: 4px; font-size: 14px; }
        .dark .input { border-color: rgb(75 85 99 / 0.6); }`}</style>
      </div>
    </div>
  );
}


function DestValueInput({ type, value, onChange, queues }) {
  if (type === 'queue') {
    return (
      <select value={value} onChange={(e) => onChange(e.target.value)} className="input">
        <option value="">— pick queue —</option>
        {queues.map((q) => (
          <option key={q.queue_id} value={q.queue_id}>{q.queue_id} · {q.name}</option>
        ))}
      </select>
    );
  }
  if (!type) {
    return <input value={value} disabled placeholder="(pick type first)" className="input" />;
  }
  const placeholder = {
    agent_extension:  '60002',
    ivr_context:      'ivr-main',
    voicemail:        '101',
    asterisk_context: 'from-internal',
  }[type] || '';
  return (
    <input value={value} onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder} className="input font-mono" />
  );
}


function Field({ label, children }) {
  return (
    <label className="block">
      <span className="block text-xs text-surface-muted dark:text-surface-dark-muted mb-1">{label}</span>
      {children}
    </label>
  );
}


// ────────────────────────────────────────────────────────────────────
// CSV import modal
// ────────────────────────────────────────────────────────────────────


function CsvImportModal({ onClose }) {
  const [file, setFile] = useState(null);
  const [diff, setDiff] = useState(null);
  const [committing, setCommitting] = useState(false);
  const [error, setError] = useState(null);

  const dry = async () => {
    setError(null); setDiff(null);
    if (!file) { setError('Pick a CSV first.'); return; }
    try {
      const r = await didsApi.importDryRun(file);
      setDiff(r);
    } catch (e) {
      setError(e.detail || e.message);
    }
  };
  const commit = async () => {
    if (!file) return;
    setCommitting(true); setError(null);
    try {
      const r = await didsApi.importCommit(file);
      setDiff(r);
    } catch (e) {
      setError(e.detail || e.message);
    } finally {
      setCommitting(false);
    }
  };

  const summary = diff?.summary || {};
  const errRows = (diff?.rows || []).filter((r) => r.action === 'error');
  const okRows = (diff?.rows || []).filter((r) => r.action === 'create' || r.action === 'update');

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <div className="w-full max-w-3xl bg-surface-panel dark:bg-surface-dark-panel border border-surface-border dark:border-surface-dark-border rounded-thin shadow-lg">
        <div className="px-4 py-3 border-b border-surface-border dark:border-surface-dark-border flex items-center justify-between">
          <h3 className="font-semibold">Bulk import DIDs (CSV)</h3>
          <button onClick={onClose} className="text-surface-muted">✕</button>
        </div>
        <div className="p-4 space-y-3 text-sm">
          <details className="text-xs text-surface-muted">
            <summary className="cursor-pointer">CSV format</summary>
            <div className="mt-2 space-y-1">
              <p>Columns: <code>number,name,carrier,dest_type,dest_value,failover_dest_type,failover_dest_value,monthly_cost,enabled,client,notes</code></p>
              <ul className="list-disc ml-5 space-y-1">
                <li><strong>number, name, dest_type, dest_value</strong> required.</li>
                <li><strong>dest_type</strong>: queue, agent_extension, ivr_context, voicemail, asterisk_context.</li>
                <li><strong>client</strong>: client name or UUID; blank = Continuum.</li>
                <li><strong>enabled</strong>: true/false (default true).</li>
              </ul>
            </div>
          </details>

          <input type="file" accept=".csv,text/csv"
            onChange={(e) => { setFile(e.target.files?.[0] || null); setDiff(null); }}
            className="text-sm" />

          {error && <div className="text-red-600 text-sm">{String(error)}</div>}

          {diff && (
            <div className="space-y-2">
              <div className="flex flex-wrap gap-3 text-xs">
                <span className="px-2 py-1 rounded-thin bg-emerald-100 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-200">
                  Create: <strong>{summary.create || 0}</strong>
                </span>
                <span className="px-2 py-1 rounded-thin bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-200">
                  Update: <strong>{summary.update || 0}</strong>
                </span>
                <span className="px-2 py-1 rounded-thin bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-200">
                  Error: <strong>{summary.error || 0}</strong>
                </span>
                {summary.created != null && (
                  <span className="px-2 py-1 rounded-thin bg-emerald-200 dark:bg-emerald-800/40 text-emerald-900 dark:text-emerald-100">
                    ✓ Created: <strong>{summary.created}</strong>
                  </span>
                )}
                {summary.updated != null && (
                  <span className="px-2 py-1 rounded-thin bg-blue-200 dark:bg-blue-800/40 text-blue-900 dark:text-blue-100">
                    ✓ Updated: <strong>{summary.updated}</strong>
                  </span>
                )}
              </div>
              {errRows.length > 0 && (
                <div className="border border-red-200 dark:border-red-800 rounded-thin overflow-hidden">
                  <table className="w-full text-xs">
                    <thead className="bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-200">
                      <tr><th className="px-2 py-1 text-left">Line</th><th className="px-2 py-1 text-left">Number</th><th className="px-2 py-1 text-left">Reason</th></tr>
                    </thead>
                    <tbody>
                      {errRows.map((r, i) => (
                        <tr key={i} className="border-t border-red-100 dark:border-red-800/50">
                          <td className="px-2 py-1 font-mono">{r.line}</td>
                          <td className="px-2 py-1 font-mono">{r.number || '—'}</td>
                          <td className="px-2 py-1 text-red-700 dark:text-red-300">{r.reason}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              {okRows.length > 0 && (
                <details className="text-xs text-surface-muted">
                  <summary className="cursor-pointer">Preview {okRows.length} change(s)</summary>
                  <table className="w-full mt-2">
                    <thead><tr><th className="px-2 py-1 text-left">#</th><th className="px-2 py-1 text-left">Action</th><th className="px-2 py-1 text-left">Number</th><th className="px-2 py-1 text-left">Dest</th></tr></thead>
                    <tbody>
                      {okRows.slice(0, 200).map((r, i) => (
                        <tr key={i}>
                          <td className="px-2 py-1 font-mono">{r.line}</td>
                          <td className="px-2 py-1">{r.action}</td>
                          <td className="px-2 py-1 font-mono">{r.number}</td>
                          <td className="px-2 py-1">{r.payload?.dest_type}:{r.payload?.dest_value}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </details>
              )}
            </div>
          )}
        </div>
        <div className="px-4 py-3 border-t border-surface-border dark:border-surface-dark-border flex justify-end gap-2">
          <button onClick={onClose} className="px-3 py-1.5 rounded-thin border border-surface-border dark:border-surface-dark-border text-sm">Close</button>
          <button onClick={dry} disabled={!file} className="px-3 py-1.5 rounded-thin border border-surface-border dark:border-surface-dark-border text-sm disabled:opacity-50">Preview</button>
          <button
            onClick={commit}
            disabled={!file || committing || (diff && (summary.create || 0) + (summary.update || 0) === 0)}
            className="px-3 py-1.5 rounded-thin bg-continuum-500 hover:bg-continuum-600 disabled:opacity-40 text-white text-sm"
          >
            {committing ? 'Committing…' : 'Commit'}
          </button>
        </div>
      </div>
    </div>
  );
}
